import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FactoryIcon from "@mui/icons-material/Factory";
import PlaceIcon from "@mui/icons-material/Place";
import ShieldIcon from "@mui/icons-material/Shield";
import { Box, InputAdornment, MenuItem, TextField } from "@mui/material";
import type { FleetSelection } from "../api/dashboard";
import type { FleetFilterOptions } from "../types";
import { OPERATING_AREAS, regionLabel } from "../utils/regions";

const SELECTORS = [
  { key: "region", label: "Region", empty: "All Regions", icon: PlaceIcon },
  {
    key: "power_plant_id",
    label: "Plant",
    empty: "All Plants",
    icon: FactoryIcon,
  },
  {
    key: "risk_category",
    label: "Risk Category",
    empty: "All Severities",
    icon: ShieldIcon,
  },
  {
    key: "spof_owner",
    label: "SPOF Owner",
    empty: "All Owners",
    icon: AccountTreeIcon,
  },
] as const;

function values(
  options: FleetFilterOptions,
  selection: FleetSelection,
  key: keyof FleetSelection,
) {
  if (key === "power_plant_id") {
    return options.plants
      .filter((plant) => !selection.region || plant.region === selection.region)
      .map((plant) => ({ id: plant.id, label: plant.name, disabled: false }));
  }
  if (key === "region") {
    const extra = options.regions.filter(
      (region) => !OPERATING_AREAS.includes(region),
    );
    return [...OPERATING_AREAS, ...extra].map((region) => ({
      id: region,
      label: regionLabel(region),
      // An area without a single imported register has nothing to show.
      disabled: !options.regions.includes(region),
    }));
  }
  const list =
    key === "risk_category" ? options.risk_categories : options.spof_owners;
  return list.map((value) => ({ id: value, label: value, disabled: false }));
}

export default function FleetFilterBar({
  options,
  selection,
  onChange,
  keys = SELECTORS.map((selector) => selector.key),
}: {
  options: FleetFilterOptions;
  selection: FleetSelection;
  onChange: (selection: FleetSelection) => void;
  keys?: readonly (keyof FleetSelection)[];
}) {
  const selectors = SELECTORS.filter((selector) => keys.includes(selector.key));
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          lg: `repeat(${selectors.length}, 1fr)`,
        },
      }}
    >
      {selectors.map(({ key, label, empty, icon: Icon }) => (
        <TextField
          key={key}
          select
          size="small"
          label={label}
          value={selection[key] ?? ""}
          onChange={(event) =>
            onChange({ ...selection, [key]: event.target.value || null })
          }
          sx={{ bgcolor: "background.paper" }}
          slotProps={{
            select: { displayEmpty: true },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Icon fontSize="small" color="action" />
                </InputAdornment>
              ),
            },
          }}
        >
          <MenuItem value="">{empty}</MenuItem>
          {values(options, selection, key).map((option) => (
            <MenuItem
              key={option.id}
              value={option.id}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ))}
    </Box>
  );
}
